// The functional Match screen (task 6.2; UI.md §5; design D-match-1..6). Composes
// the whole-field layers over the shared store: a canvas region (the
// live PixiJS world renderer is the deferred production hand-off, D-071
// — the route-independent session owner; this screen reserves and colours its
// layout seam without opening a socket), a reused `SeasonClock`, the leaderboard
// panel (`leaderboardModel`, design D-match-1), and the bottom hint pill gated on
// spectator/eliminated state (design D-match-5). It constructs no `Connection` of
// its own and drives no connection lifecycle — every dynamic value comes from the
// single store via selectors, proven server-free with a fixture-seeded store.
//
// The whole-world detail request on mount runs over the injected `lod` seam;
// `onNavigate` now drives only the explicit "⌂ Back to my hive" button — colony-match-nav
// removed the earlier zoom-threshold crossing this seam used to drive (UI.md §3/§5). Both
// mirror the `colony-screen` `ColonyController`/`INERT_CONTROLLER` precedent: every seam is
// optional with an inert default, so the still-unwired production route (before
// the route-independent session owner lands) keeps compiling and never throws —
// it just requests nothing and navigates nowhere.

import { useEffect, useRef, useState } from 'react';
import {
  selectOwnEliminated,
  selectRenderInput,
  useClientStore,
} from '../../state/store';
import type { WorldSnapshot } from '../../net/wire/connection';
import { Tier } from '../../net/wire/messages';
import type { ViewportRect } from '../../net/wire/messages';
import { SeasonClock } from '../components/hud/SeasonClock';
import { SoundControl } from '../components/hud/SoundControl';
import type { SoundControlSeams } from '../components/hud/SoundControl';
import { leaderboardModel } from '../match/leaderboard-model';
import type { LeaderboardModel } from '../match/leaderboard-model';
import { bloomRing } from '../../canvas/bloom-ring';
import type { BloomRingGeometry } from '../../canvas/bloom-ring';
import { ESTABLISHING_CAMERA, screenToWorld, type Camera } from '../../canvas/projection';
import type { WideColonyContext } from '../../canvas/renderer';
import { ReplayControls } from '../components/hud/ReplayControls';
import {
  INERT_REPLAY_CONTROL_TRANSPORT,
  type ReplayControlTransport,
} from '../../net/replay-control';
import { renderInputEquals, type RenderInput } from '../../canvas/render-input';
import {
  cameraForZoomCentre,
  panByScreenDelta,
  zoomAboutScreenPoint,
  type CameraGestureBounds,
} from '../camera/camera-gestures';
import { useCanvasCameraGestures, type CanvasCameraGestureHandlers } from '../camera/use-canvas-camera-gestures';
import { MATCH_DURATION_MIN, SIM_TICK_RATE_HZ } from '../../generated/sim-constants';
import { colonyLabel, colonyPalette } from '../match/colony-visuals';
import {
  ColonySidePanel,
  MatchTimeline,
  StatsGraphModal,
  WinnerBanner,
  buildGraphModel,
  useGraphSeriesSelection,
  type GraphSeriesKey,
  type GraphSubject,
  type PanelTypeCard,
} from '../match/MatchHud';
import { colorForType, orderRosterNames } from '../rounds/type-visuals';
import { latestForColony, useStatsHistory } from '../match/stats-history';
import { useAgentPromptFeed, type PromptEntry } from '../match/prompt-feed';
import { TimeSeriesChart } from '../components/chart/TimeSeriesChart';
import { TypeSheet } from '../components/script/TypeSheet';
import { populationFromHistogram } from '../colony/histogram-population';
import { countForType, POPULATION_UNAVAILABLE } from '../rounds/type-counts';

/** The Match screen's LOD request seam (design D-match-6): production supplies the
 * shared session's LOD controller (`requestTier` forwarding to `LodController`'s
 * requested-tier machinery, once the route-independent session owner exists,
 * D-071); tests inject a plain recorder. The inert default never requests
 * anything — matching the pre-wiring "not connected yet" reality. */
export interface MatchLodSeam {
  requestTier(tier: Tier): void;
}

const INERT_LOD: MatchLodSeam = {
  requestTier: () => {
    // Pre-wiring production default (D-071): no session owner exists yet to
    // forward a tier request to, so this deliberately does nothing.
  },
};

/** The camera pan/zoom control's LOD seam (client-match "Interactive camera pan/zoom"):
 * the `setZoom`/`setViewport` facet of the session's `LodController` (a real
 * `LodController` satisfies this), driven from OUTSIDE the canvas. Production wires it to
 * the one session's controller at the live composition (task 10.6); tests inject a
 * recorder; the inert default drives nothing (the pre-wiring reality). */
export interface MatchCameraLodSeam {
  setZoom(zoom: number): void;
  setViewport(viewport: ViewportRect): void;
}

const INERT_CAMERA_LOD: MatchCameraLodSeam = {
  setZoom: () => {
    // Pre-wiring production default: no session `LodController` to forward to yet.
  },
  setViewport: () => {},
};

// Camera zoom bounds (design "Interactive camera pan/zoom"): a wide-shot floor and a
// close-shot ceiling capping how far the wheel/pinch can travel — the bounds
// `zoomAboutScreenPoint` (camera-pan-zoom) clamps to. `match-hud` **D-1** removed the on-screen
// rail and its per-tap step constant; the *interaction* is untouched, so these stay.
const CAMERA_ZOOM_MIN = 0.1;
const CAMERA_ZOOM_MAX = 4;
const CAMERA_ZOOM_BOUNDS: CameraGestureBounds = { minScale: CAMERA_ZOOM_MIN, maxScale: CAMERA_ZOOM_MAX };

/** The world viewport rect a controlled camera (`zoom` about a world `centre`) covers,
 * derived purely from the field size — no pixel geometry (that rides the manual gate). At
 * the establishing zoom (`ESTABLISHING_CAMERA.scale`) about the field centre it is the
 * whole centred field (±fieldSize/2); zooming in shrinks it inversely about the centre.
 * Feeds ONLY the LOD-subscription seam (`cameraLod.setViewport`) — unchanged by
 * camera-pan-zoom (design.md D4): the render-facing `Camera` below is a separate,
 * measured-container derivation so the two already-covered LOD-seam scenarios
 * (`a camera pan/zoom control drives...`, `reset view restores...`) keep their exact
 * existing behaviour. */
function viewportForView(
  zoom: number,
  centre: readonly [number, number],
  fieldSize: number,
): ViewportRect {
  const half = (fieldSize / 2) * (ESTABLISHING_CAMERA.scale / zoom);
  return {
    min: [centre[0] - half, centre[1] - half],
    max: [centre[0] + half, centre[1] + half],
  };
}

/** The container's measured pixel size, or `null` if unmeasured/zero (jsdom with no
 * layout and no test stub) — the render-camera derivation above falls back to the seeded
 * `camera` prop in that case, preserving every pre-gesture test's exact existing
 * behaviour (camera-pan-zoom, design.md D2). */
function measuredSize(el: HTMLElement | null): { width: number; height: number } | null {
  if (el === null) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 ? { width: rect.width, height: rect.height } : null;
}

// The match DURATION has no wire source (tick-rate-single-source task 5.5): it stays a fixed
// display assumption, consumed from the generated mirror (**fallback-permitted** — no request
// ever publishes it) so the exported constant is not dead code the `--check` ratchet guards for
// nothing. The tick RATE does have a wire source (`manifest.tickRateHz`) and is **wire-required**
// (E4): it MUST be read from the manifest below with no defaulted fallback — a per-match balance
// preset can run at a non-15 Hz rate (D-082), and a hardcoded rate against the real
// remaining-tick count would misreport the season progress fraction.


/** The subset of `CanvasRenderer`'s API the Match screen's canvas-overlay binding
 * needs (design D-match-3/D-match-4): a real `CanvasRenderer` satisfies it
 * (`render`'s own `wideColonyContext`/`ring` params); tests inject a lightweight
 * recorder — mounting a real PixiJS `Application` needs a GPU context this
 * screen's own composition test deliberately avoids (the `InMatchCanvas`/
 * `renderer.test.ts` precedent, D10). */
export interface MatchCanvasRenderer {
  /** `signal` cancels a superseded construction (D-290) — a cancelled `init()` reclaims what it
   * has built and RESOLVES, so cancellation is never reported as a start-up failure. Optional,
   * which is what keeps every existing double that declares `init(): Promise<void>` assignable. */
  init(signal?: AbortSignal): Promise<void>;
  render(
    world: WorldSnapshot,
    camera: Camera,
    elapsedMs: number,
    tierChanged: boolean,
    // The additive render-input context (design D9) sits at the fifth position — ahead of
    // the wide-tier-only `wideColonyContext`/`ring` — so the one production
    // `CanvasRenderer` factory (which carries only the render-input on the `InMatchRenderer`
    // path) is assignable to BOTH this shape and `InMatchRenderer` (App.tsx's shared
    // "superset renderer" seam). Optional so an existing caller stays valid.
    renderInput?: RenderInput,
    wideColonyContext?: WideColonyContext,
    ring?: BloomRingGeometry | null,
  ): void;
  /** Push an independent (non-world) render-input change so it reaches the renderer within
   * one frame (design D9 / implementation-review F13) — the same contract as
   * `InMatchRenderer.updateRenderInput`, kept identical so the one production `CanvasRenderer`
   * factory is assignable to both seams. */
  updateRenderInput(renderInput: RenderInput): void;
  destroy(): void;
}

export interface MatchProps {
  /** Injectable LOD request seam (design D-match-6). Optional so the
   * still-unwired production route keeps compiling; the inert default never
   * requests anything. */
  lod?: MatchLodSeam;
  /** Injectable screen-navigation seam (design D-match-6): drives the explicit "⌂ Back
   * to my hive" button, for an active (non-eliminated, non-spectator)
   * player who navigated here via Colony's own explicit "View match" HUD control
   * (colony-match-nav, UI.md §3/§4/§5). Optional; the inert default navigates nowhere. */
  onNavigate?: (path: string) => void;
  /** Injectable wide-tier world renderer for the canvas region's colony-coloured
   * blob/hive/queen overlays + gated bloom ring (design D-match-3/D-match-4).
   * Production leaves this undefined until the route-independent session owner
   * (D-071) exists to feed it a live `WorldSnapshot` — the canvas region then
   * stays a background placeholder, never a crash, never a guessed frame; tests
   * inject a recording fake to exercise the overlay-context binding below. */
  /**
   * `tickRateHz` is **wire-required** (client-tick-rate-provenance, tick-rate-single-source
   * E4): sourced from `manifest.tickRateHz` at the point this screen actually constructs the
   * renderer — no defaulted argument.
   */
  createRenderer?: (container: HTMLElement, tickRateHz: number) => MatchCanvasRenderer;
  camera?: Camera;
  /**
   * A fixture/production-supplied convergence centre for the gated bloom ring
   * (design D-match-4). No authoritative centre streams from the server (the
   * seeded, off-centre point of DESIGN §2 is not on the wire, D-075), so this is
   * `undefined` in production — `bloomRing` then omits the ring — and only
   * fixtures/tests supply one to exercise the rendered-ring branch.
   */
  convergenceCentre?: readonly [number, number];
  /** The replay playback-control transport (client-replay; design "ReplayControls
   * transport bar"). Only used in replay mode, where the bottom bar becomes the
   * `ReplayControls` transport. Optional so a live/non-replay mount keeps compiling;
   * the inert default emits nothing (the real wire-forwarding transport wires at the
   * live composition, task 10.6). */
  replayTransport?: ReplayControlTransport;
  /** The interactive camera pan/zoom control's LOD seam (client-match "Interactive
   * camera pan/zoom"). Optional so a pre-wiring mount keeps compiling; the inert default
   * drives nothing until the live composition wires the session `LodController` (task
   * 10.6). Tests inject a recorder to prove the seam wiring. */
  cameraLod?: MatchCameraLodSeam;
  /** The in-match audio-layer seam bundle (client-audio DD6, tasks.md 6.5): the shared
   * lifecycle + preference adapter the composition-root `initAudio` threads in (task group 7).
   * Optional and additive — undefined leaves the HUD exactly as before (no control mounts),
   * mirroring the `lod`/`createRenderer` inert-seam precedent; no screen-contract change. */
  sound?: SoundControlSeams;
  /** The two-colony HUD's data seams (`match-hud` **D-3**, **D-4**, **D-7**): the live session
   * bearer the statistics and prompt reads carry, and the base URL/fetch the composition root
   * already threads into `PvpPromptFeed`. Optional and additive — undefined leaves the panels
   * without a statistics series or prompt cards rather than crashing, exactly as the other seams
   * on this screen degrade. */
  hud?: MatchHudSeams;
}

/** @see MatchProps.hud */
export interface MatchHudSeams {
  /** `player` reads `/live/prompts` (own colony only); `presenter` reads
   * `/presentation/prompts` (both). The **statistics** read is all-colonies either way — board
   * state is open, authored content is not (`match-hud` **D-4** and **D-7**). */
  mode?: 'player' | 'presenter';
  token?: string | null;
  /**
   * Colony display names known to the composition, by colony id (`spectator-hud-polish`,
   * feedback round 2).
   *
   * Names are deliberately not on the wire (D-074), so this screen's fallback chain reads
   * `Colony {id}` until a prompt carrying a name arrives — which on the projector meant both
   * panels sat nameless through the opening minutes of every match. The presentation is the one
   * composition that knows better *before* any prompt: `/presentation/status` carries each seat's
   * name from the moment it is claimed. Supplied names win over prompt-derived ones (the seat is
   * the authority the prompt name is a copy of); absent, everything reads exactly as before.
   */
  names?: Readonly<Record<number, string>>;
  /**
   * The bearer for the **statistics** read, where it differs from the prompt bearer.
   *
   * The presenter holds two distinct capabilities, not one token used on two routes: a
   * presentation bearer, which `/presentation/prompts` accepts and `/live/stats` does not
   * recognise as a session at all, and the projector's own colony-less spectator session, which is
   * a match member and therefore a legitimate reader of open board state (`pvp-ui-fixes` W2). A
   * player holds one credential that answers for both, which is why this is optional.
   *
   * Omitted ⇒ falls back to `token` (every player path, unchanged). Explicit `null` ⇒ no
   * statistics request is made at all.
   */
  statsToken?: string | null;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

export function Match({
  lod = INERT_LOD,
  onNavigate,
  createRenderer,
  camera = ESTABLISHING_CAMERA,
  convergenceCentre,
  replayTransport = INERT_REPLAY_CONTROL_TRANSPORT,
  cameraLod = INERT_CAMERA_LOD,
  sound,
  hud,
}: MatchProps) {
  const matchState = useClientStore((s) => s.matchState);
  const manifest = useClientStore((s) => s.manifest);
  // The wire rate as a PRIMITIVE subscription, so the renderer effect below re-runs exactly when
  // the rate itself changes rather than on every new `manifest` object identity. `SIM_TICK_RATE_HZ`
  // is the documented pre-handshake carve-out and nothing else: the moment Welcome publishes a
  // real rate this changes and the renderer is rebuilt on it.
  const wireTickRateHz = useClientStore((s) => s.manifest?.tickRateHz ?? SIM_TICK_RATE_HZ);
  const colonyId = useClientStore((s) => s.colonyId);
  const role = useClientStore((s) => s.role);
  const ownEliminated = useClientStore((s) => selectOwnEliminated(s));
  // Replay mode (client-match "Replay mode reuses the Match screen"): a non-null
  // replay session id is the "this is a replay" signal (store `replaySlice`, group 7).
  // Replay is a MODE of this screen — no fifth screen — driven by a replay subscription
  // in place of a live one, and is always spectator-treated (a replay viewer owns no
  // colony), so mutation affordances stay gone exactly as for any spectator.
  const isReplay = useClientStore((s) => s.replaySessionId !== null);
  const replayPlaying = useClientStore((s) => s.replayPlaying);
  const replaySpeed = useClientStore((s) => s.replaySpeed);
  const replayPosition = useClientStore((s) => s.replayPosition);
  const replayTerminalTick = useClientStore((s) => s.replayResult?.tick ?? 0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The two-colony HUD's own store reads (`match-hud`): the own colony's live registry and
  // per-type histogram, and the terminal result the winner banner is driven by.
  const colonyMeta = useClientStore((s) => s.colonyMeta);
  const histogram = useClientStore((s) => s.histogram);
  const matchResult = useClientStore((s) => s.matchResult);

  // DESIGN §14 balance seed this display-only screen falls back to absent a streamed
  // manifest (mirrors `TICK_RATE_HZ_FALLBACK` above) — hoisted above the mount effect
  // below so that effect can reset the LOD viewport to the whole field at the field size
  // known at mount time.
  const fieldSize = manifest?.fieldSize ?? 4096;

  // Entering the Match screen keeps the complete rounds-style detail tier — once, on mount,
  // over the injected seam — AND resets the subscription viewport to the whole centred field
  // in the SAME effect (so the two land in one `LodController` coalescing window, colony-
  // match-nav review finding): `requestTier` only ever changes `requested.tier`, preserving
  // whatever `requested.viewport` the PREVIOUS screen last set (`net/wire/lod.ts`). Colony
  // narrows that viewport to a small hive-sized rect (colony-match-nav); without this reset,
  // navigating Colony → Match would retain Colony's bounded viewport. Reset it to the whole field
  // while retaining the rounds-style complete detail tier.
  // `lod`/`cameraLod`'s identity is a stable DI seam (injected once by the caller), not a
  // reactive per-render prop.
  useEffect(() => {
    lod.requestTier(Tier.Close);
    cameraLod.setViewport(viewportForView(ESTABLISHING_CAMERA.scale, [0, 0], fieldSize));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The canvas region's colony-coloured overlay binding (design D-match-3/
  // D-match-4): only mounts when a renderer is injected (production leaves it
  // undefined until D-071's route-independent session owner exists to feed a
  // live `WorldSnapshot`). When present, every store `currentWorld` update is
  // redrawn with the manifest-derived colony context and the gated bloom ring —
  // this reads the store only; it opens no `Connection` and drives no connection
  // lifecycle.
  //
  // `renderer`/`lastRenderTime`/`drawFrame` are hoisted to refs (camera-pan-zoom, design.md
  // D2, mirroring `InMatchCanvas`'s D4 ref-hoisting) so the separate camera-reactive effect
  // further down can redraw through the SAME renderer instance without depending on (or
  // rebuilding) this mount effect.
  //
  // A torn-down invocation nulls `rendererRef`, so the null check IS the disposal guard —
  // there is deliberately no component-level `disposed` flag to fall out of step with it (the
  // ReviewFrame precedent, WAG-31/WAG-32; the same fix `InMatchCanvas` carries). Cancellation
  // is a per-invocation LOCAL in the mount effect below.
  const rendererRef = useRef<MatchCanvasRenderer | null>(null);
  const lastRenderTimeRef = useRef<number | null>(null);
  const drawFrameRef = useRef<(world: WorldSnapshot) => void>(() => undefined);
  const cameraRef = useRef<Camera>(camera);

  useEffect(() => {
    if (createRenderer === undefined) {
      return;
    }
    const container = containerRef.current;
    if (container === null) {
      return;
    }

    // **wire-required** (client-tick-rate-provenance, tick-rate-single-source E4), sourced
    // from `manifest.tickRateHz`. A spectator/reload can reach this screen before Welcome
    // lands, so — matching `InMatchCanvas`'s identical construction-time gap — this takes the
    // **pre-handshake render** carve-out for that one instant rather than inventing a wire
    // value that was never published; once a real `manifest` exists it is never ignored.
    //
    // "That one instant" is load-bearing, and used not to be true. This read was
    // `useClientStore.getState()` — a snapshot — while the effect's deps were `[createRenderer]`,
    // so a mount that happened before Welcome kept the mirror's rate for the WHOLE mount: at a
    // 30 Hz server the presentation clock interpolated and the corpse windows quantised at 15 Hz
    // forever. `PresentationClock` and `CorpseTracker` both take the rate as a `private readonly`
    // constructor argument, so following a late rate means rebuilding the renderer — which is why
    // `wireTickRateHz` is a reactive subscription and a dependency below.
    const tickRateHz = wireTickRateHz;

    function drawFrame(world: WorldSnapshot): void {
      const renderer = rendererRef.current;
      if (renderer === null) {
        return;
      }
      const state = useClientStore.getState();
      const wideColonyContext: WideColonyContext | undefined = state.manifest
        ? {
            fieldSize: state.manifest.fieldSize,
            colonyIds: state.manifest.colonyIds,
            // The play margin (`deathmatch-world` D-6) rides beside `fieldSize`, never
            // redefining it: `fieldSize` still drives LOD viewport derivation and blob
            // decoding, while the margin only says where the perimeter is drawn.
            playMarginU: state.manifest.playMarginU,
          }
        : undefined;
      const ring = state.matchState
        ? bloomRing(state.matchState.state.viabilityRadius, convergenceCentre)
        : null;
      const now = performance.now();
      const elapsedMs = lastRenderTimeRef.current === null ? 0 : now - lastRenderTimeRef.current;
      lastRenderTimeRef.current = now;
      // The additive render-input context (design D9), fed from the store selectors at
      // this call site (canvas internals never read the store). It precedes the
      // wide-tier-only `wideColonyContext`/`ring` in the shared `render()` signature.
      // Defaults to a live-play context when no anchor/season slice is set.
      const renderInput = selectRenderInput(state);
      // **A draw failure must never escape this function** — see `InMatchCanvas`'s twin for
      // the full account. The store subscription below runs inside zustand's unguarded
      // `listeners.forEach`, so a throw would skip every remaining listener (the composition
      // root's own navigation subscription among them) and propagate up through
      // `Connection.applyOrGap`, which calls `onWorld` before `sendAck()`. A broken renderer
      // must not also cost the client its acks and its match lifecycle.
      try {
        renderer.render(world, cameraRef.current, elapsedMs, false, renderInput, wideColonyContext, ring);
      } catch (drawError: unknown) {
        console.error('waggle: the match screen renderer failed to draw a frame', drawError);
      }
    }
    drawFrameRef.current = drawFrame;

    // Cancellation is **per invocation**, captured by this closure — see `InMatchCanvas`'s
    // twin of this block for the full account. In short: the component-level `disposed` ref
    // this replaces could not tell "this invocation was superseded" from "a later invocation
    // is alive", so under `StrictMode`'s mount → cleanup → mount the superseded construction
    // escaped destruction, published itself over `rendererRef`, and left a second orphaned
    // canvas (a leaked Pixi application, WebGL context and RAF loop) stacked in the container.
    const built = createRenderer(container, tickRateHz);
    let cancelled = false;
    // The renderer's own cancellation channel (**D-290**), per invocation exactly as `cancelled`
    // is: that flag tells this closure it was superseded, the controller tells the RENDERER,
    // which is the only party that can reclaim a canvas it has not finished attaching.
    const controller = new AbortController();
    // Immediate and idempotent: `CanvasRenderer.init()` appends its canvas and only *then*
    // awaits `assets.load()`, so a superseded invocation holds a live context for the whole of
    // that await and must give it back at cleanup, not when its assets happen to land. With the
    // abort in place a renderer honouring the signal has already unwound by then, so these calls
    // are a free second pass — see `InMatchCanvas`'s twin for why they stay for now.
    const teardown = (): void => {
      try {
        built.destroy();
      } catch (teardownError: unknown) {
        console.error('waggle: the match screen renderer failed to tear down', teardownError);
      }
    };
    void built.init(controller.signal).then(
      () => {
        if (cancelled) {
          teardown();
          return;
        }
        rendererRef.current = built;
        const world = useClientStore.getState().currentWorld;
        if (world !== null) {
          // `drawFrame` reports its own failures and never throws, so the first draw cannot
          // become an unhandled rejection here — and a trailing `.catch` would be wrong
          // anyway, unable to tell a draw failure from the cancelled path's own teardown.
          drawFrame(world);
        }
      },
      // A rejected init is never "nothing was built" — the canvas is appended before the load
      // that rejected. Two-argument `then`, not a trailing `.catch`, so a throw out of the
      // fulfilment handler cannot destroy a renderer already published to `rendererRef`.
      // A **cancellation never arrives here**: a cancelled `init()` resolves (D-290), so an
      // ordinary teardown is never reported as a start-up failure.
      (error: unknown) => {
        teardown();
        console.error('waggle: the match screen renderer failed to start', error);
      },
    );

    // Redraw when EITHER the `currentWorld` reference changes OR `lastSeq` advances
    // (both driven by the root's `onWorld`) — neither alone is sufficient
    // (field-render-visibility fix + implementation-review F1; mirrors the
    // `InMatchCanvas` subscription):
    //  - The production `Connection` mutates one `WorldSnapshot` in place across a
    //    delta stream (stable reference, advancing seq), so a reference-only predicate
    //    misses every delta — the seq clause catches those.
    //  - A fresh/reconnect keyframe installs a NEW `WorldSnapshot` reference, but the
    //    server resets its per-connection sequence to 0, so a reconnect keyframe can
    //    reuse the retained `lastSeq` (both 0) — a seq-only predicate then misses it
    //    and the field freezes on the stale snapshot; the reference clause catches it.
    const unsubscribe = useClientStore.subscribe((state, prev) => {
      if (rendererRef.current === null) {
        return;
      }
      if (
        state.currentWorld !== null &&
        (state.currentWorld !== prev.currentWorld || state.lastSeq !== prev.lastSeq)
      ) {
        // A world update: `drawFrame` reads the latest render-input too.
        drawFrame(state.currentWorld);
        return;
      }
      // No world delta this update: push an independent render-input change (season / anchor /
      // lifecycle / epoch) so the RAF loop reflects it within one frame even while the stream
      // stalls (implementation-review F13). Does not re-ingest a world.
      const nextInput = selectRenderInput(state);
      if (!renderInputEquals(nextInput, selectRenderInput(prev))) {
        // Guarded for the same reason `drawFrame` is: inside zustand's unguarded notify loop.
        try {
          rendererRef.current.updateRenderInput(nextInput);
        } catch (pushError: unknown) {
          console.error('waggle: the match screen renderer failed to take a render input', pushError);
        }
      }
    });

    return () => {
      cancelled = true;
      // Tell the renderer too (D-290): `rendererRef` is null until `init()` resolves, so the
      // teardown below cannot reach a construction still in flight — the abort can.
      controller.abort();
      unsubscribe();
      lastRenderTimeRef.current = null;
      // Only relinquish the ref if THIS invocation is the one holding it.
      if (rendererRef.current === built) {
        rendererRef.current = null;
      }
      teardown();
    };
    // `createRenderer`/`convergenceCentre` are stable DI seams (injected once by the
    // caller), not reactive per-render props — mirrors `InMatchCanvas`'s own
    // effect-dependency discipline. `camera` is deliberately EXCLUDED (design.md D2/D4):
    // it is handled by the separate reactive-redraw effect below.
    //
    // `wireTickRateHz` IS a dependency: the rate is a constructor argument to the presentation
    // clock and the corpse tracker, so a rate that arrives after mount can only be honoured by
    // rebuilding. It is a primitive, and it changes at most once per connection (the carve-out
    // fallback → the published wire rate), so this is one rebuild, not a per-render churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createRenderer, wireTickRateHz]);

  // D-082: derive from the manifest's real tick rate (never the seed-15Hz fallback
  // alone), so a non-default balance preset's tick rate does not skew the fraction.
  // `manifest` is `null` only in the brief pre-Welcome window, exactly like `matchState` — no
  // fallback rate: absent a real wire-required `tickRateHz` there is nothing honest to compute a
  // progress fraction from, so it reads as "no progress yet" (0), the same as `matchState` being
  // absent already reads.
  const totalMatchTicks = manifest !== null ? MATCH_DURATION_MIN * 60 * manifest.tickRateHz : null;
  const progress =
    matchState && totalMatchTicks !== null ? 1 - matchState.state.clockRemainingTicks / totalMatchTicks : 0;
  const model: LeaderboardModel | null = matchState
    ? leaderboardModel(matchState, colonyId, manifest?.colonyIds ?? null)
    : null;
  const spectating = ownEliminated || role === 'spectator' || isReplay;

  // ---------------------------------------------------------------------------------
  // The two-colony HUD (`match-hud` D-3..D-8)
  // ---------------------------------------------------------------------------------

  // An explicit `undefined` check, not `??`: `null` is a meaningful value here ("make no
  // statistics request"), and `??` would silently promote it to the prompt bearer — which is the
  // exact capability confusion `statsToken` exists to prevent.
  const statsSamples = useStatsHistory({
    token: hud?.statsToken !== undefined ? hud.statsToken : (hud?.token ?? null),
    baseUrl: hud?.baseUrl,
    fetchImpl: hud?.fetchImpl,
  });
  const promptMode = hud?.mode ?? 'player';
  const prompts = useAgentPromptFeed({
    mode: promptMode,
    token: hud?.token ?? null,
    baseUrl: hud?.baseUrl,
    fetchImpl: hud?.fetchImpl,
  });
  const { selected: graphSeriesSelection, toggle: toggleGraphSeries } = useGraphSeriesSelection();
  // Which colony's statistics modal is open, or `null` for none. A colony id rather than a
  // boolean because the modal answers for the panel that opened it: tapping Red's graph is a
  // question about Red, and being shown Blue's charts underneath is noise the viewer then
  // scrolls past. The other colony stays one tap away on its own panel.
  const [graphOpen, setGraphOpen] = useState<number | null>(null);
  const [selectedType, setSelectedType] = useState<{ colonyId: number; card: PanelTypeCard } | null>(
    null,
  );

  // The colony roster this screen is drawing. `manifest.colonyIds` when Welcome has landed,
  // otherwise whatever the scoreboard and eliminations name between them — the same fallback
  // `leaderboardModel` already takes, so the layout decision and the leaderboard cannot disagree
  // about how many colonies there are.
  const colonyIds: number[] =
    manifest?.colonyIds ??
    (matchState
      ? Array.from(
          new Set([
            ...matchState.state.scoreboard.map((row) => row.colonyId),
            ...matchState.state.eliminations,
          ]),
        ).sort((a, b) => a - b)
      : []);
  // **The one conditional** (D-3): exactly two colonies gets the two-sided layout; three or more
  // keeps the existing leaderboard untouched, so live mode's 150-colony shape survives intact.
  const twoColony = colonyIds.length === 2;

  /** A colony's display name: the composition's own knowledge first (the presentation's seat
   * names, `hud.names`), else the most recent prompt author for it (player display names are not
   * on the wire, D-074), else the deterministic `colonyLabel`. */
  function labelForColony(id: number): string {
    const supplied = hud?.names?.[id];
    if (supplied !== undefined && supplied !== '') return supplied;
    for (let i = prompts.length - 1; i >= 0; i -= 1) {
      const prompt = prompts[i];
      if (prompt.colony === id && prompt.name !== '') return prompt.name;
    }
    return colonyLabel(id, colonyId);
  }

  /** Prompts this caller may read for `id`, or `undefined` where scoping withholds them (D-7):
   * a player reads only their own colony, a presenter reads both. Never an empty list standing in
   * for "not allowed" — the section is absent instead. */
  function promptsForColony(id: number): readonly PromptEntry[] | undefined {
    if (promptMode !== 'presenter' && id !== colonyId) return undefined;
    return prompts.filter((prompt) => prompt.colony === id);
  }

  /**
   * A colony's type roster in a stable order — the own colony's registry order where there is one,
   * else the names the stats sample carries. The **position** is what `colorForType` needs for a
   * player-defined type (the extension ramp is indexed, never keyed by name, since names are
   * colony-private), so panel chips and graph bands must agree on it or the same type would wear
   * two colours on one screen.
   */
  function typeNamesForColonyRoster(id: number): string[] {
    if (id === colonyId && colonyMeta !== null) {
      return colonyMeta.types.map((type) => type.name);
    }
    const sample = latestForColony(statsSamples, id);
    // Name-keyed compositions arrive alphabetised (serde `BTreeMap`) — an artefact, not an
    // order. Re-sorted onto the canonical roster so an opponent's chips read in the same
    // sequence as the player's own dock.
    return sample === null ? [] : orderRosterNames(Object.keys(sample.composition));
  }

  function typeColourForColony(id: number): (name: string) => string {
    const roster = typeNamesForColonyRoster(id);
    return (name) => colorForType(name, Math.max(0, roster.indexOf(name)));
  }

  function typeCardsForColony(id: number): PanelTypeCard[] {
    const colourOf = typeColourForColony(id);
    // The viewer's OWN colony has the live registry and the ~1 Hz per-type histogram; an opponent
    // is known through the stats sample's name-keyed composition, which carries no type id (and,
    // per D-9's default, therefore opens Stats only).
    if (id === colonyId && colonyMeta !== null) {
      const population = populationFromHistogram(histogram);
      return colonyMeta.types.map((type) => ({
        typeId: type.typeId,
        name: type.name,
        count: population.available ? countForType(population, type.typeId) : null,
        colour: colourOf(type.name),
      }));
    }
    const sample = latestForColony(statsSamples, id);
    if (sample === null) return [];
    return typeNamesForColonyRoster(id).map((name) => ({
      typeId: null,
      name,
      count: sample.composition[name] ?? 0,
      colour: colourOf(name),
    }));
  }

  /**
   * A colony's living worker population at the latest sample — the denominator the type sheet's
   * "share of colony" is a fraction of, and the figure the graph modal names beside the colony.
   *
   * Own colony prefers the ~1 Hz histogram (finer than the stats poll); an opponent is the sum of
   * its composition. Both exclude the queen, who is typeless (DESIGN §3) and belongs to no type —
   * so the chips sum to exactly this number, by construction rather than by coincidence.
   */
  function workerPopulationForColony(id: number): number | null {
    if (id === colonyId) {
      const population = populationFromHistogram(histogram);
      if (population.available) return population.total;
    }
    const sample = latestForColony(statsSamples, id);
    if (sample === null) return null;
    return Object.values(sample.composition).reduce((sum, count) => sum + count, 0);
  }

  function graphSubject(id: number): GraphSubject {
    const shared = {
      samples: statsSamples,
      colonyId: id,
      colour: colonyPalette(id),
      typeColour: typeColourForColony(id),
      labelBands: true,
      promptTicks: (promptsForColony(id) ?? []).map((p) => ({ cursor: p.cursor, tick: p.tick })),
    };
    // **Two models, one per unit** (`spectator-hud-polish` W8). The toggles still choose what is
    // plotted; they no longer choose what shares a y axis, because nothing does: a count and an
    // amount get their own charts.
    const only = (...keys: GraphSeriesKey[]): Set<GraphSeriesKey> =>
      new Set(keys.filter((key) => graphSeriesSelection.has(key)));
    return {
      colonyId: id,
      label: labelForColony(id),
      colour: colonyPalette(id),
      population: workerPopulationForColony(id),
      composition: buildGraphModel({ ...shared, selected: only('types') }),
      nectar: buildGraphModel({ ...shared, selected: only('nectar', 'capacity') }),
    };
  }

  const graphSubjects = twoColony ? colonyIds.map(graphSubject) : [];
  const graphTotalTicks = Math.max(
    1,
    ...graphSubjects.flatMap((s) => [s.composition.totalTicks, s.nectar.totalTicks]),
  );

  // The winner (D-8): the terminal result's rank-0 placement, whatever produced it.
  const winner =
    matchResult === null ? null : [...matchResult.placements].sort((a, b) => a.rank - b.rank)[0] ?? null;

  const ownColonyDocument =
    colonyMeta === null ? '' : colonyMeta.types.map((type) => type.canonicalText).join('\n');

  // Interactive camera pan/zoom (client-match "Interactive camera pan/zoom"): the
  // controlled camera is a world `centre` + `zoom` held here and driven onto the
  // injected LOD seam (`setZoom`/`setViewport`) from OUTSIDE the canvas, AND (camera-
  // pan-zoom, design.md D2 — the core reported-bug fix) onto the renderer's actual
  // `Camera`, via `cameraRef` below. Offered to EVERY viewer of this screen
  // (bee-inspect-panel, closing D-145): it was gated on `spectating` back when the only
  // way an active player saw this screen was mid-zoom-gesture, but `colony-match-nav` made
  // the visit a deliberate navigation ("View match"), so the gate left exactly the viewer
  // who chose to survey the field unable to move the camera. This screen carries no
  // colony-mutation affordance in any state, so ungating grants nothing but looking around.
  const [cameraView, setCameraView] = useState<{ centre: [number, number]; zoom: number }>({
    centre: [0, 0],
    zoom: ESTABLISHING_CAMERA.scale,
  });

  function driveCamera(next: { centre: [number, number]; zoom: number }): void {
    setCameraView(next);
    cameraLod.setZoom(next.zoom);
    cameraLod.setViewport(viewportForView(next.zoom, next.centre, fieldSize));
  }

  function zoomBy(factor: number): void {
    const zoom = Math.min(CAMERA_ZOOM_MAX, Math.max(CAMERA_ZOOM_MIN, cameraView.zoom * factor));
    driveCamera({ centre: cameraView.centre, zoom });
  }

  // `match-hud` **D-1** removed the reset-to-establishing rail button along with the rest of the
  // camera chrome. The establishing frame is still what the screen mounts at (the mount effect
  // above) and what a zoom-out reaches; only the button is gone.

  // The render-facing camera (camera-pan-zoom, design.md D2): re-derived from the
  // MEASURED container whenever `cameraView` changes (a gesture, rail tap, or reset), and
  // an explicit redraw fires through the ref-hoisted `drawFrame` — no new world tick is
  // needed to see a pan/zoom. Falls back to the seeded `camera` prop when the container is
  // unmeasured (jsdom with no layout and no test stub), preserving every pre-gesture
  // test's exact existing behaviour.
  useEffect(() => {
    const size = measuredSize(containerRef.current);
    cameraRef.current = size !== null ? cameraForZoomCentre(cameraView.centre, cameraView.zoom, size) : camera;
    if (rendererRef.current === null) {
      return;
    }
    const world = useClientStore.getState().currentWorld;
    if (world !== null) {
      drawFrameRef.current(world);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraView]);

  // The gesture handlers passed to `useCanvasCameraGestures` (camera-pan-zoom, design.md
  // D1): a STABLE object whose methods are reassigned every render (never a fresh object
  // literal), so the hook's own effect — which attaches native listeners keyed on this
  // object's identity — never tears down and reattaches on every gesture-driven re-render
  // (each `onZoom`/`onPan` call always reads the LATEST assigned closure).
  const gestureHandlersRef = useRef<CanvasCameraGestureHandlers>({ onZoom: () => {}, onPan: () => {} });
  gestureHandlersRef.current.onZoom = (screenPoint, factor) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0 || rect.height <= 0) {
      // Unmeasured container (jsdom, no stub): fall back to the rail's own
      // container-centre zoom rather than a divide-by-zero camera.
      zoomBy(factor);
      return;
    }
    const localPoint = { x: screenPoint.x - rect.left, y: screenPoint.y - rect.top };
    const current = cameraForZoomCentre(cameraView.centre, cameraView.zoom, { width: rect.width, height: rect.height });
    const next = zoomAboutScreenPoint(current, localPoint, factor, CAMERA_ZOOM_BOUNDS);
    const nextCentre = screenToWorld(next, { x: rect.width / 2, y: rect.height / 2 });
    driveCamera({ centre: nextCentre, zoom: next.scale });
  };
  gestureHandlersRef.current.onPan = (dxPx, dyPx) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0 || rect.height <= 0) {
      return; // Unmeasured container: no jsdom safety net for pan pixel geometry (D-086).
    }
    const current = cameraForZoomCentre(cameraView.centre, cameraView.zoom, { width: rect.width, height: rect.height });
    const panned = panByScreenDelta(current, dxPx, dyPx);
    const nextCentre = screenToWorld(panned, { x: rect.width / 2, y: rect.height / 2 });
    driveCamera({ centre: nextCentre, zoom: cameraView.zoom });
  };

  // Native non-passive wheel listener + preventDefault + pointer-capture drag (camera-
  // pan-zoom, design.md D1): browser-zoom suppression was always unconditional over the
  // canvas (plan.md D2), and since D-145 closed the gesture DRIVE is unconditional too —
  // `enabled` is `true` for spectator, replay viewer, and active player alike.
  useCanvasCameraGestures(containerRef, gestureHandlersRef.current, true);

  return (
    <div data-testid="match-screen" style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Canvas region (UI.md §5): the live wide-tier world renderer is the
          deferred production hand-off (D-071, route-independent session owner)
          — this reserves the layered background seam without opening a socket. */}
      <div
        ref={containerRef}
        data-testid="match-canvas"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--field-bg, #7FA65B)',
          // Browser-zoom/scroll suppression is UNCONDITIONAL over the canvas (camera-
          // pan-zoom, design.md D2/plan.md D2) — independent of whether a gesture
          // currently drives the camera (`useCanvasCameraGestures`'s non-passive wheel
          // listener + preventDefault does the rest).
          touchAction: 'none',
          // Every viewer drags this canvas now (D-145 closed), so every viewer gets the
          // grab affordance.
          cursor: 'grab',
        }}
      />

      {/* Replay mode badge (client-match "a visible replay indicator"): rides where
          the season clock sits and distinguishes a replay from a live spectate. Only
          rendered in replay mode; the season clock steps down a row to make room. */}
      {isReplay && (
        <div
          data-testid="replay-indicator"
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '6px 12px',
            background: 'var(--info-soft)',
            border: '1.5px solid var(--info)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-hud)',
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 4, flex: 'none', background: 'var(--info)' }} />
          <b
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 12,
              color: 'var(--info)',
              textTransform: 'uppercase',
              letterSpacing: '.07em',
            }}
          >
            Replay
          </b>
        </div>
      )}

      {/* Top-left SeasonClock (same component as Colony, UI.md §5). */}
      <div data-testid="season-clock" style={{ position: 'absolute', top: isReplay ? 56 : 12, left: 12 }}>
        <SeasonClock progress={Math.max(0, Math.min(1, progress))} />
      </div>

      {/* Top-right stack: the leaderboard panel (design D-match-1) with the SoundControl
          tucked under it (client-audio DD6 placement sketch), at Colony↔Match corner parity.
          The column is the absolutely-positioned top-right anchor; its children flow statically.

          **At exactly two colonies the leaderboard is replaced by the two side panels**
          (`match-hud` **D-3**) — one conditional, one screen. Above two it is unchanged, so
          live mode's 150-colony shape survives intact. The SoundControl stays in either case
          (sound is not a mutation, and a spectator keeps it). */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          right: twoColony ? 292 : 12,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 8,
        }}
      >
      {!twoColony && (
      <div
        data-testid="leaderboard-panel"
        style={{
          width: 210,
          background: 'var(--hud-bg)',
          border: '1.5px solid var(--hud-line)',
          borderRadius: 14,
          boxShadow: 'var(--shadow-hud)',
          padding: '10px 12px',
        }}
      >
        <div
          style={{
            fontWeight: 800,
            fontSize: 9,
            textTransform: 'uppercase',
            letterSpacing: '.08em',
            color: 'var(--hud-text-dim)',
            marginBottom: 6,
          }}
        >
          Colonies · {model?.header ?? '— of — alive'}
        </div>
        {model?.rows.map((row) => (
          <div
            key={row.colonyId}
            data-testid={`leaderboard-row-${row.colonyId}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minHeight: 30,
              opacity: row.isEliminated ? 0.5 : 1,
            }}
          >
            <span
              style={{ width: 10, height: 10, borderRadius: 5, flex: 'none', background: row.colour }}
            />
            <b
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                fontSize: 13,
                color: row.isOwn ? 'var(--accent-strong)' : 'var(--hud-text)',
                flex: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.isOwn ? '▸ ' : ''}
              {row.label}
            </b>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 13,
                color: 'var(--hud-text-dim)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {row.livingCount === null ? '—' : row.livingCount}
            </span>
          </div>
        ))}
      </div>
      )}
        {/* SoundControl (client-audio DD6): under the leaderboard, right-aligned — spectators
            keep it (sound is not a mutation). Mounts only once the audio seam is wired (task
            group 7) — additive, no screen-contract change. */}
        {sound && (
          <div data-testid="sound-control-mount">
            <SoundControl lifecycle={sound.lifecycle} prefs={sound.prefs} />
          </div>
        )}
      </div>

      {/* `match-hud` **D-1** — the developer chrome is gone. `CameraControl` (the LOD tier
          readout and the zoom rail) is NOT mounted here, and neither is the bottom hint pill
          with its "Spectating" indicator. **Zoom itself is untouched**: scroll, trackpad pinch
          and two-finger pinch still drive `driveCamera`, which still drives the LOD subscription
          (`cameraLod.setZoom`/`setViewport`) — DESIGN §9 makes zoom the unifying interaction, and
          only the on-screen controls were presenter chrome. `CameraControl` itself is untouched
          and still mounts on the Colony screen. */}

      {/* Explicit navigation back to the own colony (colony-match-nav, UI.md §3/§5): the
          reciprocal of Colony's "View match" HUD control, and the one piece of the removed pill
          that is game chrome rather than developer chrome — removing it would have stranded an
          active player on this screen with no way home. Withheld for a spectator, an eliminated
          player and a replay viewer, who have no hive to return to. */}
      {!spectating && !isReplay && (
        <button
          type="button"
          data-testid="match-back-home"
          onClick={() => onNavigate?.('/colony')}
          style={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: 44,
            padding: '0 16px',
            borderRadius: 999,
            border: '1.5px solid var(--hud-line)',
            background: 'var(--hud-bg)',
            boxShadow: 'var(--shadow-hud)',
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: 13,
            color: 'var(--accent-strong)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          ⌂ Back to my hive
        </button>
      )}

      {/* The two-sided player panels (**D-3**), rendered only at exactly two colonies. */}
      {twoColony &&
        colonyIds.map((id, index) => {
          const sample = latestForColony(statsSamples, id);
          const scoreboardRow = matchState?.state.scoreboard.find((row) => row.colonyId === id);
          const subject = graphSubjects[index];
          return (
            <ColonySidePanel
              key={id}
              colonyId={id}
              side={index === 0 ? 'left' : 'right'}
              label={labelForColony(id)}
              // **Never a hardcoded red and blue** (D-3): the token resolves through the base
              // `--colony-N` ramp, and the presentation installs its own values over slots 0/1.
              colour={colonyPalette(id)}
              bees={scoreboardRow?.livingCount ?? sample?.population ?? null}
              nectar={scoreboardRow?.pool ?? sample?.storedNectar ?? null}
              capacity={sample?.capacity ?? null}
              types={typeCardsForColony(id)}
              onOpenType={(card) => setSelectedType({ colonyId: id, card })}
              onOpenGraph={() => setGraphOpen(id)}
              graph={
                // The inline summary is the **composition alone**: 64px of height cannot carry
                // two units, and it is the one that answers "what is this colony made of". It
                // carries no axis furniture at this size — the labels would out-measure the plot
                // — so it reads as a sparkline into the modal, which has the scales.
                <TimeSeriesChart
                  testId={`colony-graph-inline-${id}`}
                  series={subject.composition.series}
                  totalTicks={graphTotalTicks}
                  maxValue={subject.composition.maxValue}
                  width={240}
                  height={64}
                  bandGapPx={1}
                  ariaLabel={`${labelForColony(id)} bees by type`}
                />
              }
              prompts={promptsForColony(id)}
              tickRateHz={manifest?.tickRateHz ?? null}
            />
          );
        })}

      {/* The expanded graph (**D-6**, narrowed by `spectator-hud-polish`): the modal answers for
          the ONE colony whose panel opened it. It used to stack both players — with the deep-dive
          split each subject is two charts, so both at once was four plots deep, and the question
          the tap asks is about the panel under the finger. Both colonies' charts still share the
          x domain (`graphTotalTicks` spans both), so switching panels compares like with like. */}
      {graphOpen !== null && twoColony && (
        <StatsGraphModal
          subjects={graphSubjects.filter((subject) => subject.colonyId === graphOpen)}
          selected={graphSeriesSelection}
          onToggle={toggleGraphSeries}
          onClose={() => setGraphOpen(null)}
          totalTicks={graphTotalTicks}
          tickRateHz={manifest?.tickRateHz ?? null}
        />
      )}

      {/* The bee-type sheet, opened from a panel card (**D-3**). **D-9's stated default**: an
          opponent's card opens with the Stats tab ONLY. Counts are board state; an authored
          script is the player's own work, closer to a prompt than to a scoreboard row — and
          opponent scripts are not on the wire in any case, so Code and Flowchart would have
          nothing honest to render. */}
      {selectedType !== null && (
        <TypeSheet
          typeId={selectedType.card.typeId ?? -1}
          typeName={selectedType.card.name}
          color={colonyPalette(selectedType.colonyId)}
          colonyDocument={selectedType.colonyId === colonyId ? ownColonyDocument : ''}
          tabs={selectedType.colonyId === colonyId ? undefined : ['stats']}
          /**
           * **Sized to what it shows** (`spectator-hud-polish`, feedback round 2). The sheet's own
           * geometry is the sandbox's code-editing surface — `inset: 16px 16px 228px`, nearly the
           * whole viewport, with a bottom clearance for a control stack this screen does not have.
           * A stats-only opening holds two figures and a note, so it gets a centred card instead;
           * the own-colony opening keeps the full sheet, because its Code and Flowchart tabs are
           * the thing the size was for.
           */
          style={
            selectedType.colonyId === colonyId
              ? undefined
              : {
                  top: '50%',
                  left: '50%',
                  right: 'auto',
                  bottom: 'auto',
                  transform: 'translate(-50%, -50%)',
                  width: 420,
                  maxWidth: 'calc(100vw - 32px)',
                  maxHeight: 440,
                }
          }
          /**
           * **The whole colony, not the tapped type** (`spectator-hud-polish` W6).
           *
           * This used to build the population from the card alone — `byType` a single entry and
           * `total` that same count — so the sheet's "Share of colony" divided a type by itself
           * and read **100% for every type on this screen**, own colony and opponent alike. The
           * denominator has to be the colony's living workers, which
           * {@link workerPopulationForColony} already resolves for the panel heading: the
           * histogram where the viewer owns the colony, the summed composition otherwise. The
           * queen is excluded from both, so "share of colony" means share of *workers* — the same
           * set the chips sum to.
           */
          population={
            selectedType.card.count === null
              ? POPULATION_UNAVAILABLE
              : (() => {
                  const total = workerPopulationForColony(selectedType.colonyId);
                  return total === null
                    ? POPULATION_UNAVAILABLE
                    : {
                        available: true as const,
                        byType: new Map([[selectedType.card.typeId ?? -1, selectedType.card.count]]),
                        total,
                      };
                })()
          }
          onClose={() => setSelectedType(null)}
        />
      )}

      {/* The finish (**D-8**). */}
      {winner !== null && (
        <WinnerBanner
          label={labelForColony(winner.colonyId)}
          colour={colonyPalette(winner.colonyId)}
        />
      )}

      {/* Bottom-centre transport (design D-match-5 / "ReplayControls transport bar"): in replay
          mode the bottom bar is the `ReplayControls` transport (Pause/Resume + Restart + speed
          selector over a read-only position strip); otherwise it is the live timeline and
          countdown (**D-2**), which is equally read-only — live replay reaches tick N by
          re-simulating, so seek is O(N) and deferred (D-123, DESIGN §15). Zoom/pan are never
          restricted either way — the Match screen carries no mutation affordances in any state. */}
      {isReplay && manifest !== null ? (
        <ReplayControls
          transport={replayTransport}
          playing={replayPlaying}
          speed={replaySpeed}
          position={replayPosition}
          terminalTick={replayTerminalTick}
          // **wire-required** (client-tick-rate-provenance E4): no defaulted argument — the
          // `manifest !== null` guard above is what makes this read honest rather than a
          // silently substituted 15.
          tickRateHz={manifest.tickRateHz}
        />
      ) : isReplay ? null : (
        <MatchTimeline
          progress={progress}
          remainingTicks={matchState?.state.clockRemainingTicks ?? 0}
          // **wire-required**: `null` before Welcome reads as the placeholder rather than a time
          // invented at the seed 15 Hz.
          tickRateHz={manifest?.tickRateHz ?? null}
          // The stream's wall-clock pacing (WAG-107). The deathmatch runs its 30-minute season
          // arc at 2x, so this is what makes the countdown read the 15 minutes a player is
          // actually here for. Season `progress` above deliberately does not multiply — it is a
          // fraction of the sim's own arc.
          speedMultiplier={manifest?.speedMultiplier ?? null}
        />
      )}
    </div>
  );
}
